import {
  addDaysUTC,
  addMonthsUTC,
  calculateMonthlyHealthTarget,
  calculateProjection,
  calculateWalletBalanceSnapshot,
  endOfUTCMonth,
  getNextMovement,
  getProjectionStatus,
  summarizeMonth,
  toDateKeyUTC,
  type EntityRef,
  type FinanceTransaction,
  type TransactionOverride as CoreTransactionOverride,
} from '@financego/core';

import type { AgendaValidation, Category, Transaction, Wallet } from '../../lib/api-types';
import type { DashboardSnapshot } from './dashboard.types';

interface DashboardInput {
  readonly agenda: readonly AgendaValidation[];
  readonly categories: readonly Category[];
  readonly today: Date | string;
  readonly transactions: readonly Transaction[];
  readonly wallets: readonly Wallet[];
}

export function toCoreTransactions(
  transactions: readonly Transaction[],
  categories: readonly Category[],
  wallets: readonly Wallet[],
): readonly FinanceTransaction[] {
  const categoryNames = new Map(categories.map((category) => [category.id, category.name]));
  const walletNames = new Map(wallets.map((wallet) => [wallet.id, wallet.name]));

  return transactions.map((transaction) => ({
    active: transaction.active,
    amount: transaction.amount,
    category: toEntityRef(transaction.categoryId, categoryNames),
    date: transaction.date,
    frequency: transaction.frequency,
    id: transaction.id,
    limitDate: transaction.limitDate,
    note: transaction.note,
    overrides: transaction.overrides.map((override) => {
      const coreOverride: CoreTransactionOverride = {
        date: override.date,
        disabled: override.disabled,
        ...(override.additionalNote === undefined
          ? {}
          : { additionalNote: override.additionalNote }),
        ...(override.amount === undefined ? {} : { amount: override.amount }),
        ...(override.walletId === undefined
          ? {}
          : { wallet: toEntityRef(override.walletId, walletNames) }),
      };
      return coreOverride;
    }),
    toWallet:
      transaction.toWalletId === null ? null : toEntityRef(transaction.toWalletId, walletNames),
    type: transaction.type,
    wallet: toEntityRef(transaction.walletId, walletNames),
  }));
}

function toEntityRef(id: string, names: ReadonlyMap<string, string>): EntityRef {
  const name = names.get(id);
  return name === undefined ? { id } : { id, name };
}

export function deriveDashboardSnapshot(input: DashboardInput): DashboardSnapshot {
  const today = toDateKeyUTC(input.today);
  const coreTransactions = toCoreTransactions(input.transactions, input.categories, input.wallets);
  const occurrenceOptions = { validations: input.agenda };
  const openingWallets = input.wallets.map((wallet) => ({
    ...wallet,
    balance: wallet.type === 'credit' || wallet.type === 'loan' ? wallet.balance : 0,
  }));
  const historicalSnapshot = calculateWalletBalanceSnapshot(coreTransactions, {
    ...occurrenceOptions,
    targetDate: addDaysUTC(today, -1),
    wallets: openingWallets,
  });
  const effectiveWallets = input.wallets.map((wallet) => ({
    ...wallet,
    balance: historicalSnapshot.walletBalances[wallet.id] ?? 0,
  }));
  const projection = calculateProjection(coreTransactions, {
    ...occurrenceOptions,
    monthsAhead: 6,
    today,
  });
  const currentWalletBalance = historicalSnapshot.globalBalance;
  const adjustedPoints = projection.points.map((point) => ({
    ...point,
    balance: point.balance + currentWalletBalance,
  }));
  const endMonthDate = toDateKeyUTC(endOfUTCMonth(today));
  const threeMonthDate = toDateKeyUTC(addMonthsUTC(today, 3));
  const threeMonthPoint =
    adjustedPoints.find((point) => point.date === threeMonthDate) ?? adjustedPoints.at(-1)!;
  const sixMonthPoint = adjustedPoints.at(-1)!;
  const now = new Date(`${today}T00:00:00.000Z`);
  const displayProjection = {
    endMonthDate,
    endMonthBalance: projection.endMonthBalance + currentWalletBalance,
    lowPoint: {
      balance: projection.lowPoint.balance + currentWalletBalance,
      date: projection.lowPoint.date,
    },
    points: adjustedPoints,
    sixMonthBalance: sixMonthPoint.balance,
    sixMonthDate: sixMonthPoint.date,
    threeMonthBalance: threeMonthPoint.balance,
    threeMonthDate: threeMonthPoint.date,
    todayBalance: projection.todayBalance + currentWalletBalance,
  };

  return {
    agenda: input.agenda,
    categories: input.categories,
    coreTransactions,
    currentWalletBalance,
    displayProjection,
    healthTarget: calculateMonthlyHealthTarget(coreTransactions, {
      ...occurrenceOptions,
      today,
      wallets: effectiveWallets,
    }),
    monthSummary: summarizeMonth(
      coreTransactions,
      now.getUTCFullYear(),
      now.getUTCMonth() + 1,
      occurrenceOptions,
    ),
    nextMovement: getNextMovement(coreTransactions, today, occurrenceOptions),
    openingWallets,
    projectionStatus: getProjectionStatus(displayProjection),
    rawTransactions: input.transactions,
    today,
    wallets: effectiveWallets,
  };
}
